/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createEscalationPolicy } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type EscalationPolicyDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function EscalationPolicyDialog({ actionDialogReturn, onNavigate: _onNavigate }: EscalationPolicyDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { setWorkspaceVersion } = useWorkspace();
  const { toast } = useFeedback();

  if (!actionDialog || actionDialog.kind !== 'escalation-policy') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit = actionDialog.teamHandle.trim().length > 0;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'escalation-policy' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      await createEscalationPolicy(session.accessToken, {
        organizationId: actionDialog.organizationId,
        teamHandle: actionDialog.teamHandle.trim(),
        appliesToCategory: actionDialog.appliesToCategory.trim() || undefined
      });
      setWorkspaceVersion((v) => v + 1);
      toast('success', 'Escalation policy created');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">On-call</div>
            <h3>New escalation policy</h3>
            <p>Define who gets notified, and in what order, when an alert fires for a team.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Team handle</span>
            <input
              value={actionDialog.teamHandle}
              onChange={(e) => setActionDialog({ ...actionDialog, teamHandle: e.target.value })}
              placeholder="platform-eng"
              autoFocus
            />
          </label>
          <label className="settingsField">
            <span>
              Applies to category{' '}
              <em style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</em>
            </span>
            <input
              value={actionDialog.appliesToCategory}
              onChange={(e) => setActionDialog({ ...actionDialog, appliesToCategory: e.target.value })}
              placeholder="e.g. infra, billing"
            />
            <p className="fieldHint">Leave blank to make this the team&apos;s default policy.</p>
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create policy'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { EscalationPolicyDialog };
